import { Grid } from '../../core/Grid.js';
import { Direction, Position, positionKey, oppositeDir } from '../../core/GridGeometry.js';
import { SplittingGame, BeamSegment, BeamSimulation } from './SplittingGame.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface SplittingRenderConfig extends Partial<RenderConfig> {
    splitterColor?: string;
    beamWidth?: number;
    terminalDotRadius?: number;
}

const SPLITTING_DEFAULTS = {
    splitterColor: COLORS.teal,
    beamWidth: 2,
    terminalDotRadius: 4
};

// ── Splitter animation (docs/juice.md) ────────────────────────────────
//
// A splitter has only two states, so there is no `change` to make — it arrives
// and it leaves. The diamond grows from the cell centre, which is the point the
// beam actually splits at, rather than being stroked on like the line-shaped
// pieces in other games: at this size a perimeter sweep reads as a shimmer.
//
// The `replay` is where the game's own idea shows. The clicked cell plays the
// split: the beam runs in over the first half, and *both* perpendicular beams
// push out together over the second.

interface SplitterTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    /** Exit only: the colour it had on the last frame it existed. */
    color: string;
}

export class SplittingRenderer extends GridRenderer {
    private splittingConfig: typeof SPLITTING_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private splitterTweens = new Map<string, SplitterTween>();
    private splitterState = new Set<string>();          // what the last frame drew
    private splitterColors = new Map<string, string>(); // last colour, for removals
    private splittersSynced: boolean = false;           // first frame seeds, never animates
    /** The cell replaying its split, and when it started. */
    private splitPlay: { cell: Position; start: number } | null = null;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: SplittingGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: SplittingRenderConfig = {}) {
        super(canvas, grid, config);
        this.splittingConfig = { ...SPLITTING_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every splitter and beam at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.splitterTweens.clear();
        this.splitPlay = null;
    }

    render(game: SplittingGame): void {
        const grid = game.getGrid();
        const sim = game.simulate();

        this.lastGame = game;
        this.animating = false;
        // Before the beams: the diff is what starts the split replay, and the beams
        // are drawn first.
        this.syncSplitterTweens(game.getSplitters());

        this.clear();
        this.drawGrid();

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawEntryMarker();
        this.drawRouteHighlight(game);

        // The clicked cell replays its split. The beams are drawn as long runs, so
        // the cell is clipped out of them and its own crossing drawn separately.
        const replay = this.splitReplay();
        if (replay) {
            this.ctx.save();
            this.clipOutCells([replay.cell]);
        }
        this.drawBeams(sim);
        if (replay) {
            this.ctx.restore();
            this.drawSplitCrossing(sim, replay.cell, replay.p);
        }

        this.drawSplitterTriangles(game, sim);
        this.drawTeleportLines(sim);
        if (isVortexGame(game)) { this.drawActiveVortexGates(game); }
        this.drawSplitters(game, sim);
        this.drawDigitsWithIndicators(grid, game);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Cell replay (docs/juice.md) ───────────────────────────────────

    /** The cell replaying its split this frame, and how far in it is. */
    private splitReplay(): { cell: Position; p: number } | null {
        if (!this.splitPlay) return null;
        const { value, done } = this.tweens.tween(this.splitPlay.start, MOTION.replay);
        if (done) {
            this.splitPlay = null;
            return null;
        }
        this.animating = true;
        return { cell: this.splitPlay.cell, p: value };
    }

    /**
     * One cell's worth of beam, `p` of the way through: everything arriving runs in
     * to the centre over the first half, and everything leaving pushes out over the
     * second — so a splitter's one-in-two-out reads as the split it is.
     */
    private drawSplitCrossing(sim: BeamSimulation, cell: Position, p: number): void {
        const { cellSize } = this.config;
        const half = cellSize / 2;
        const center = this.getCellCenter(cell);
        const inT = Math.min(1, p * 2);
        const outT = Math.max(0, p * 2 - 1);
        const at = (pos: Position) => pos.row === cell.row && pos.col === cell.col;

        this.ctx.lineWidth = this.splittingConfig.beamWidth;
        this.ctx.lineCap = 'round';

        const stroke = (
            pieces: Array<[{ x: number; y: number }, { x: number; y: number }]>, beamId: number
        ) => {
            this.ctx.strokeStyle = this.resolveBeamColor(sim.beamColor.get(beamId) ?? 'teal');
            for (const [a, b] of pieces) {
                this.ctx.beginPath();
                this.ctx.moveTo(a.x, a.y);
                this.ctx.lineTo(b.x, b.y);
                this.ctx.stroke();
            }
        };
        const edgePoint = (dir: Direction, reach: number) => {
            const v = GridRenderer.DIR_VEC[dir];
            return { x: center.x + v.x * reach, y: center.y + v.y * reach };
        };

        // The board's own beam enters at the top-left, from outside the grid.
        if (cell.row === 0 && cell.col === 0) {
            this.ctx.strokeStyle = COLORS.teal;
            const entry = edgePoint('W', half);
            for (const [a, b] of this.crossingPieces(cell, entry, null, inT)) {
                this.ctx.beginPath();
                this.ctx.moveTo(a.x, a.y);
                this.ctx.lineTo(b.x, b.y);
                this.ctx.stroke();
            }
        }

        for (const seg of sim.segments) {
            if (sim.terminalBeamIds.has(seg.beamId)) continue;
            const dir = this.segmentDirection(seg);
            if (at(seg.to)) {
                stroke(this.crossingPieces(cell, edgePoint(oppositeDir(dir), half), null, inT), seg.beamId);
            }
            if (at(seg.from)) {
                stroke(this.crossingPieces(cell, null, edgePoint(dir, half), outT), seg.beamId);
            }
        }

        // Beams that leave without reaching anything are drawn as stubs, shortened
        // where two share an edge — match that, or they'd twitch as the replay ends.
        const exitCount = new Map<string, number>();
        for (const t of sim.terminalBeams) {
            const key = `${positionKey(t.originPosition)},${t.exitDirection}`;
            exitCount.set(key, (exitCount.get(key) ?? 0) + 1);
        }
        for (const t of sim.terminalBeams) {
            if (!at(t.originPosition)) continue;
            const key = `${positionKey(t.originPosition)},${t.exitDirection}`;
            const reach = half - ((exitCount.get(key) ?? 0) > 1 ? 10 : 4);
            stroke(this.crossingPieces(cell, null, edgePoint(t.exitDirection, reach), outT), t.beamId);
        }
    }

    /**
     * Diff the board against the last frame: a placed splitter grows in, a removed
     * one shrinks away, and a single change also sets that cell replaying its split.
     */
    private syncSplitterTweens(splitters: Set<string>): void {
        if (!this.tweens.isEnabled()) {
            this.splitterTweens.clear();
            this.splitterState = new Set(splitters);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would place the whole thing again.
        if (!this.splittersSynced) {
            this.splitterState = new Set(splitters);
            this.splittersSynced = true;
            return;
        }

        const added: string[] = [];
        const removed: string[] = [];
        for (const key of splitters) if (!this.splitterState.has(key)) added.push(key);
        for (const key of this.splitterState) if (!splitters.has(key)) removed.push(key);
        if (added.length === 0 && removed.length === 0) return;

        // Fan out from the top-left entry, the way the beam sets off.
        const rank = (key: string) => {
            const [row, col] = key.split(',').map(Number);
            return (row + col) * this.grid.size + row;
        };
        const changed = [...added, ...removed].sort((a, b) => rank(a) - rank(b));

        const now = this.tweens.now();
        // One cell changing is a click, and it replays its split. A bulk change
        // (Reset, Restore best) isn't a question about any one cell.
        if (changed.length === 1) {
            const [row, col] = changed[0].split(',').map(Number);
            this.splitPlay = { cell: { row, col }, start: now };
        } else {
            this.splitPlay = null;
        }

        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (splitters.has(key)) {
                this.splitterTweens.set(key, { kind: 'enter', start, color: '' });
            } else {
                this.splitterTweens.set(key, {
                    kind: 'exit', start,
                    color: this.splitterColors.get(key) ?? this.splittingConfig.splitterColor
                });
            }
        });

        this.splitterState = new Set(splitters);
    }

    // ── Helpers ───────────────────────────────────────────────────────

    private segmentDirection(seg: BeamSegment): Direction {
        if (seg.to.row < seg.from.row) return 'N';
        if (seg.to.row > seg.from.row) return 'S';
        if (seg.to.col < seg.from.col) return 'W';
        return 'E';
    }

    private resolveBeamColor(name: string): string {
        switch (name) {
            case 'teal': return COLORS.teal;
            case 'amber': return COLORS.amber;
            case 'red': return COLORS.red;
            default: return COLORS.teal;
        }
    }

    private isRimEdge(pos: Position, edgeDir: Direction): boolean {
        switch (edgeDir) {
            case 'N': return pos.row === 0;
            case 'S': return pos.row === this.grid.size - 1;
            case 'W': return pos.col === 0;
            case 'E': return pos.col === this.grid.size - 1;
        }
    }

    // ── Digits ────────────────────────────────────────────────────────

    private drawDigitsWithIndicators(grid: Grid, game: SplittingGame): void {
        const { digitColor, digitFont, cellSize } = this.config;
        const sim = game.simulate();

        // Build a map of digit -> distance for quick lookup
        const digitDistances = new Map<number, number>();
        for (const hit of sim.digitsHit) {
            if (!digitDistances.has(hit.digit)) {
                digitDistances.set(hit.digit, hit.distance);
            }
        }

        // Build a map of digit -> whether it's in correct order. A digit counts
        // as in-order only if it matches its expected rank AND is strictly farther
        // than the previous one; a tie breaks the chain (consistent with checkWin),
        // so tied digits no longer render as correct.
        const digitInOrder = new Map<number, boolean>();
        for (let i = 0; i < sim.digitsHit.length; i++) {
            const hit = sim.digitsHit[i];
            const strictlyFurther = i === 0 || hit.distance > sim.digitsHit[i - 1].distance;
            if (hit.digit === grid.minDigit + i && strictlyFurther) {
                digitInOrder.set(hit.digit, true);
            } else {
                break;
            }
        }

        const positions = grid.getAllDigitPositions();
        for (const [digit, pos] of positions) {
            const center = this.getCellCenter(pos);

            // Draw white diamond around digit to show it splits beams
            const size = cellSize * 0.35;
            this.ctx.strokeStyle = COLORS.white;
            this.ctx.lineWidth = 2;
            this.ctx.beginPath();
            this.ctx.moveTo(center.x, center.y - size);
            this.ctx.lineTo(center.x + size, center.y);
            this.ctx.lineTo(center.x, center.y + size);
            this.ctx.lineTo(center.x - size, center.y);
            this.ctx.closePath();
            this.ctx.stroke();

            // Fill with semi-transparent white
            this.ctx.fillStyle = colorWithAlpha('white', 0.1);
            this.ctx.fill();

            // Draw the digit (with small Y offset for better visual centering)
            this.ctx.fillStyle = digitColor;
            this.ctx.font = digitFont;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(digit.toString(), center.x, center.y + 2);

            // Draw distance indicator badge if this digit has been reached
            const distance = digitDistances.get(digit);
            if (distance !== undefined) {
                const inOrder = digitInOrder.get(digit) ?? false;
                const badgeColor = inOrder ? this.splittingConfig.splitterColor : COLORS.amber;
                this.drawBadge(pos, 'NE', distance.toString(), badgeColor);
            }
        }
    }

    // ── Entry Marker ──────────────────────────────────────────────────

    private drawEntryMarker(): void {
        this.drawRimMarker({
            position: { row: 0, col: 0 },
            color: COLORS.teal,
            entry: true,
            edge: 'left'
        });
    }

    // ── Splitters ─────────────────────────────────────────────────────

    /** Scoring overlay: redraw the splitters (primary) in amber. */
    protected drawScoringPrimary(game: SplittingGame): void {
        this.drawSplitters(game, null, COLORS.amber);
    }

    private drawSplitters(game: SplittingGame, sim: BeamSimulation | null, colorOverride?: string): void {
        const splitters = game.getSplitters();
        const { cellSize } = this.config;
        const size = cellSize * 0.3;

        // The scoring overlay redraws the splitters in a flat colour on a frozen
        // board: it reads state, it doesn't advance it. (The diff itself already
        // ran, at the top of render.)
        const live = colorOverride === undefined;
        const now = this.tweens.now();

        this.ctx.lineWidth = 2;

        const diamond = (key: string, color: string, fill: string, scale: number) => {
            if (scale <= 0.01) return;
            const [row, col] = key.split(',').map(Number);
            const center = this.getCellCenter({ row, col });
            const s = size * scale;

            this.ctx.strokeStyle = color;
            this.ctx.beginPath();
            this.ctx.moveTo(center.x, center.y - s);
            this.ctx.lineTo(center.x + s, center.y);
            this.ctx.lineTo(center.x, center.y + s);
            this.ctx.lineTo(center.x - s, center.y);
            this.ctx.closePath();
            this.ctx.stroke();

            this.ctx.fillStyle = fill;
            this.ctx.fill();
        };

        for (const key of splitters) {
            const isInLoop = sim?.splittersInLoop.has(key) ?? false;
            const color = colorOverride ?? (isInLoop ? COLORS.amber : this.splittingConfig.splitterColor);
            const fill = (colorOverride || isInLoop) ? colorWithAlpha('amber', 0.3) : colorWithAlpha('teal', 0.3);
            if (live) this.splitterColors.set(key, color);

            const tween = live ? this.splitterTweens.get(key) : undefined;
            let scale = 1;
            if (tween) {
                const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
                if (done) this.splitterTweens.delete(key);
                else {
                    this.animating = true;
                    // The `exit` curve runs the disappearance, so invert it.
                    scale = tween.kind === 'enter' ? value : 1 - value;
                }
            }
            diamond(key, color, fill, scale);
        }

        // Splitters being removed are already gone from game state — they only exist
        // here, shrinking back into the point they split the beam at.
        for (const [key, tween] of live ? this.splitterTweens : []) {
            if (tween.kind !== 'exit' || splitters.has(key)) continue;
            const { value, done } = this.tweens.tween(tween.start, MOTION.exit, now);
            if (done) {
                this.splitterTweens.delete(key);
                this.splitterColors.delete(key);
                continue;
            }
            this.animating = true;
            diamond(key, tween.color, colorWithAlpha('teal', 0.3), 1 - value);
        }
    }

    // ── Beams ─────────────────────────────────────────────────────────

    /**
     * Clicking a digit traces the light back to where it came in: the route from the
     * entry to that digit, laid under the beams as a wide blue band.
     *
     * Under, and wider, rather than over: the beam colours carry the ordering (teal
     * in sequence, amber from the last correct digit, red out of order) and tinting
     * them would be saying something about the answer. The band reads as a channel
     * the beam runs along, and every beam keeps the colour it had.
     *
     * Blue is the colour of things the player is pointing at rather than things the
     * board is telling them — the same blue as Turns' path highlight, which this is
     * the sibling of. It is static: it answers a click about what already exists,
     * and nothing arrived or changed for a motion to describe.
     */
    private drawRouteHighlight(game: SplittingGame): void {
        const route = game.getSelectedBeamRoute();
        if (route.segments.length === 0) return;
        const { padding, cellSize } = this.config;

        // The entry stub, so the route starts where the light does rather than at the
        // first cell centre. Its beam is the root of every lineage, so it is always on.
        const start = this.getCellCenter({ row: 0, col: 0 });
        const lines = [{ from: { x: padding - 10, y: start.y }, to: start }];
        for (const seg of route.segments) {
            lines.push({ from: this.getCellCenter(seg.from), to: this.getCellCenter(seg.to) });
        }
        for (const tp of route.teleports) {
            lines.push({ from: this.getCellCenter(tp.from), to: this.getCellCenter(tp.to) });
        }

        this.ctx.save();
        this.ctx.strokeStyle = colorWithAlpha('blue', 0.55);
        this.ctx.lineWidth = cellSize * 0.13;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        // One path, one stroke, however many pieces. Consecutive segments meet at a
        // cell centre and their round caps overlap there — stroked separately, the
        // translucent blue would double up and bead at every centre the route passes
        // through. A single stroke composites the whole band once.
        //
        // Gapped over digit cells exactly as the beams are, so a digit on the way
        // stays legible and the band never sits on top of one.
        this.ctx.beginPath();
        for (const piece of this.clipSegmentsAtDigits(lines)) {
            this.ctx.moveTo(piece.from.x, piece.from.y);
            this.ctx.lineTo(piece.to.x, piece.to.y);
        }
        this.ctx.stroke();

        this.ctx.restore();
    }

    private drawBeams(sim: BeamSimulation): void {
        const { padding } = this.config;

        this.ctx.lineWidth = this.splittingConfig.beamWidth;
        this.ctx.lineCap = 'round';

        // Draw teal entry line from outside to (0,0)
        const startCenter = this.getCellCenter({ row: 0, col: 0 });
        this.ctx.strokeStyle = COLORS.teal;
        this.ctx.beginPath();
        this.ctx.moveTo(padding - 10, startCenter.y);
        this.ctx.lineTo(startCenter.x, startCenter.y);
        this.ctx.stroke();

        // Draw beam segments with per-beam colors (skip terminal beams)
        for (const segment of sim.segments) {
            if (sim.terminalBeamIds.has(segment.beamId)) continue;

            const colorName = sim.beamColor.get(segment.beamId) ?? 'teal';
            this.ctx.strokeStyle = this.resolveBeamColor(colorName);
            this.ctx.lineWidth = this.splittingConfig.beamWidth;
            this.ctx.lineCap = 'round';

            // Gap the beam over digit cells so their T-split isn't drawn — the
            // diamond + edge triangles mark the split instead. User-placed splitters
            // sit on non-digit cells, so their T is kept.
            this.strokeLineClippedAtDigits(this.getCellCenter(segment.from), this.getCellCenter(segment.to));
        }

        // Draw first half-cell for terminal beams (center to cell edge)
        // Build set of edges with double exits to shorten their beams
        const { cellSize } = this.config;
        const doubleExitEdges = new Set<string>();
        const exitEdgeCount = new Map<string, number>();
        for (const terminal of sim.terminalBeams) {
            const edgeKey = `${positionKey(terminal.originPosition)},${terminal.exitDirection}`;
            const count = (exitEdgeCount.get(edgeKey) ?? 0) + 1;
            exitEdgeCount.set(edgeKey, count);
            if (count > 1) doubleExitEdges.add(edgeKey);
        }

        for (const terminal of sim.terminalBeams) {
            const pos = terminal.originPosition;
            if (pos.row < 0 || pos.row >= this.grid.size ||
                pos.col < 0 || pos.col >= this.grid.size) continue;

            const edgeKey = `${positionKey(pos)},${terminal.exitDirection}`;
            const halfCell = cellSize / 2 - (doubleExitEdges.has(edgeKey) ? 10 : 4);

            const colorName = sim.beamColor.get(terminal.beamId) ?? 'teal';
            this.ctx.strokeStyle = this.resolveBeamColor(colorName);
            this.ctx.lineWidth = this.splittingConfig.beamWidth;
            this.ctx.lineCap = 'round';

            const center = this.getCellCenter(pos);
            let edgeX = center.x, edgeY = center.y;
            switch (terminal.exitDirection) {
                case 'N': edgeY = center.y - halfCell; break;
                case 'S': edgeY = center.y + halfCell; break;
                case 'W': edgeX = center.x - halfCell; break;
                case 'E': edgeX = center.x + halfCell; break;
            }

            this.strokeLineClippedAtDigits(center, { x: edgeX, y: edgeY });
        }
    }

    // ── Splitter & Digit Triangles ────────────────────────────────────

    private drawSplitterTriangles(game: SplittingGame, sim: BeamSimulation): void {
        const splitters = game.getSplitters();
        const grid = game.getGrid();

        // Cells that get triangles: splitters, digit positions, and active vortex gates
        const specialCells = new Set<string>(splitters);
        for (const [, pos] of grid.getAllDigitPositions()) {
            specialCells.add(positionKey(pos));
        }
        if (isVortexGame(game)) {
            for (const key of game.getActiveGatePairs().keys()) {
                specialCells.add(key);
            }
        }

        // Events store drawing params + grouping key
        // drawBeamEdgeTriangle expects (refPos, travelDir, inbound) where the triangle
        // is at refPos's travelDir edge. For entries Mirrors-style, refPos = segment.from.
        interface TriangleEvent {
            drawPos: Position;
            drawDir: Direction;
            inbound: boolean;
            color: string;
        }

        // Group by physical edge + type: "targetCellKey,edgeDir,entry|exit"
        const events = new Map<string, TriangleEvent[]>();

        const addEvent = (groupKey: string, event: TriangleEvent) => {
            if (!events.has(groupKey)) events.set(groupKey, []);
            events.get(groupKey)!.push(event);
        };

        // From non-terminal segments
        for (const segment of sim.segments) {
            if (sim.terminalBeamIds.has(segment.beamId)) continue;

            const dir = this.segmentDirection(segment);
            const colorName = sim.beamColor.get(segment.beamId) ?? 'teal';
            const color = this.resolveBeamColor(colorName);
            const fromKey = positionKey(segment.from);
            const toKey = positionKey(segment.to);

            // Exit triangle: segment departs FROM a special cell
            if (specialCells.has(fromKey)) {
                addEvent(`${fromKey},${dir},exit`, {
                    drawPos: segment.from, drawDir: dir, inbound: false, color
                });
            }

            // Entry triangle: segment arrives AT a special cell
            // Use segment.from + travelDir (Mirrors convention) so rotation is correct
            if (specialCells.has(toKey)) {
                addEvent(`${toKey},${oppositeDir(dir)},entry`, {
                    drawPos: segment.from, drawDir: dir, inbound: true, color
                });
            }
        }

        // From terminal beams (these exit from splitters/digits toward the rim)
        for (const terminal of sim.terminalBeams) {
            const key = positionKey(terminal.originPosition);
            if (!specialCells.has(key)) continue;
            if (terminal.originPosition.row < 0 || terminal.originPosition.row >= this.grid.size ||
                terminal.originPosition.col < 0 || terminal.originPosition.col >= this.grid.size) continue;

            const colorName = sim.beamColor.get(terminal.beamId) ?? 'teal';
            const color = this.resolveBeamColor(colorName);
            addEvent(`${key},${terminal.exitDirection},exit`, {
                drawPos: terminal.originPosition, drawDir: terminal.exitDirection,
                inbound: false, color
            });
        }

        // Draw all events
        for (const [eventKey, eventList] of events) {
            const parts = eventKey.split(',');
            const targetPos = { row: parseInt(parts[0]), col: parseInt(parts[1]) };
            const edgeDir = parts[2] as Direction;
            // parts[3] is 'entry' or 'exit'
            const isRim = this.isRimEdge(targetPos, edgeDir);

            if (isRim) {
                this.drawRimDots(targetPos, edgeDir, eventList.map(e => e.color));
            } else if (eventList.length > 1) {
                this.drawDoubleBeamEdgeTriangles(
                    eventList[0].drawPos, eventList[0].drawDir,
                    eventList.map(e => e.color), eventList[0].inbound
                );
            } else {
                const e = eventList[0];
                this.drawBeamEdgeTriangle(e.drawPos, e.drawDir, e.color, e.inbound);
            }
        }
    }

    // ── Triangle Drawing (ported from MirrorsRenderer) ────────────────

    private drawBeamEdgeTriangle(
        from: Position, direction: Direction, color: string, inbound: boolean
    ): void {
        const { padding, cellSize } = this.config;
        const center = this.getCellCenter(from);

        let x: number, y: number;
        let angle: number;

        switch (direction) {
            case 'S':
                x = center.x;
                y = padding + (from.row + 1) * cellSize;
                angle = 0;
                break;
            case 'N':
                x = center.x;
                y = padding + from.row * cellSize;
                angle = Math.PI;
                break;
            case 'E':
                x = padding + (from.col + 1) * cellSize;
                y = center.y;
                angle = -Math.PI / 2;
                break;
            case 'W':
                x = padding + from.col * cellSize;
                y = center.y;
                angle = Math.PI / 2;
                break;
        }

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        if (inbound) {
            this.ctx.moveTo(-5, 1);
            this.ctx.lineTo(5, 1);
            this.ctx.lineTo(0, 9);
        } else {
            this.ctx.moveTo(-5, -9);
            this.ctx.lineTo(5, -9);
            this.ctx.lineTo(0, -1);
        }
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    private drawDoubleBeamEdgeTriangles(
        pos: Position, direction: Direction, colors: string[], inbound: boolean
    ): void {
        const { padding, cellSize } = this.config;
        const center = this.getCellCenter(pos);
        const offset = 5;

        let x: number, y: number;
        let angle: number;
        let offsetX = 0, offsetY = 0;

        switch (direction) {
            case 'S':
                x = center.x; y = padding + (pos.row + 1) * cellSize; angle = 0;
                offsetX = offset;
                break;
            case 'N':
                x = center.x; y = padding + pos.row * cellSize; angle = Math.PI;
                offsetX = offset;
                break;
            case 'E':
                x = padding + (pos.col + 1) * cellSize; y = center.y; angle = -Math.PI / 2;
                offsetY = offset;
                break;
            case 'W':
                x = padding + pos.col * cellSize; y = center.y; angle = Math.PI / 2;
                offsetY = offset;
                break;
        }

        for (let i = 0; i < Math.min(colors.length, 2); i++) {
            const sign = i === 0 ? -1 : 1;
            this.ctx.save();
            this.ctx.translate(x + sign * offsetX, y + sign * offsetY);
            this.ctx.rotate(angle);

            this.ctx.fillStyle = colors[i];
            this.ctx.beginPath();
            if (inbound) {
                this.ctx.moveTo(-5, 1);
                this.ctx.lineTo(5, 1);
                this.ctx.lineTo(0, 9);
            } else {
                this.ctx.moveTo(-5, -9);
                this.ctx.lineTo(5, -9);
                this.ctx.lineTo(0, -1);
            }
            this.ctx.closePath();
            this.ctx.fill();

            this.ctx.restore();
        }
    }

    // ── Rim Dots (for edges on the grid boundary) ─────────────────────

    private drawRimDots(pos: Position, edgeDir: Direction, colors: string[]): void {
        const { cellSize } = this.config;
        const center = this.getCellCenter(pos);
        const halfCell = cellSize / 2;

        let dotX = center.x, dotY = center.y;
        switch (edgeDir) {
            case 'N': dotY = center.y - halfCell; break;
            case 'S': dotY = center.y + halfCell; break;
            case 'W': dotX = center.x - halfCell; break;
            case 'E': dotX = center.x + halfCell; break;
        }

        if (colors.length > 1) {
            // Double dots with orthogonal offset
            const offset = 4;
            let oX = 0, oY = 0;
            if (edgeDir === 'N' || edgeDir === 'S') { oX = offset; }
            else { oY = offset; }

            for (let i = 0; i < 2; i++) {
                const sign = i === 0 ? -1 : 1;
                this.ctx.fillStyle = colors[i];
                this.ctx.beginPath();
                this.ctx.arc(dotX + sign * oX, dotY + sign * oY,
                    this.splittingConfig.terminalDotRadius, 0, Math.PI * 2);
                this.ctx.fill();
            }
        } else {
            this.ctx.fillStyle = colors[0];
            this.ctx.beginPath();
            this.ctx.arc(dotX, dotY, this.splittingConfig.terminalDotRadius, 0, Math.PI * 2);
            this.ctx.fill();
        }
    }

    // ── Teleport Lines ────────────────────────────────────────────────

    private drawTeleportLines(sim: BeamSimulation): void {
        if (sim.teleportSegments.length === 0) return;

        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = 3;
        this.ctx.setLineDash([6, 4]);
        this.ctx.lineCap = 'round';
        this.ctx.globalAlpha = 0.6;

        const drawn = new Set<string>();
        for (const seg of sim.teleportSegments) {
            const pairKey = [
                `${seg.from.row},${seg.from.col}`,
                `${seg.to.row},${seg.to.col}`
            ].sort().join('|');
            if (drawn.has(pairKey)) continue;
            drawn.add(pairKey);

            const from = this.getCellCenter(seg.from);
            const to = this.getCellCenter(seg.to);
            this.ctx.beginPath();
            this.ctx.moveTo(from.x, from.y);
            this.ctx.lineTo(to.x, to.y);
            this.ctx.stroke();
        }

        this.ctx.setLineDash([]);
        this.ctx.globalAlpha = 1.0;
    }
}
